import { useState, useMemo, useRef } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'
import Button from 'react-md/lib/Buttons'
import styled from 'styled-components'
import * as notifyActions from 'state/notifyActions'
import type {
	ContractKey,
	ContractSort,
} from 'ui/ManagerFinance/Contracts/types'
import { InvoicingMethod } from 'ui/ManagerFinance/enum'
import { useBuildings } from 'ui/CompanyBuildings/hooks'
import { createApartmentOptions } from '@viilu-web/shared/util/apartmentOptions'
import type { SelectOptions } from 'ui/ManagerFinance/types'
import { invoicingMethodMessage } from 'ui/ManagerFinance/utils/messages'
import ContractTable from 'ui/ManagerFinance/Contracts/ContractTable'
import useContractsByApartment from 'ui/ManagerFinance/Contracts/useContractsByApartments'
import { _deleteContract } from 'state/contract-actions'
import { GENERIC_ERROR_NOTIFICATION } from 'ui/messages'
import { useDispatch } from 'react-redux'
import useDialogs from 'ui/components/dialogs/useDialogs'
import ContractDialog from 'ui/ManagerFinance/Contracts/ContractDialog'
import { useCompanyApartments } from 'util/hooks/useCompanyApartments'
import { createPartyOptions } from 'ui/ManagerFinance/utils/partyOptions'
import useParties from 'ui/ManagerFinance/Parties/useParties'
import FontIcon from 'react-md/lib/FontIcons'
import ExpansionPanel, { ExpansionList } from 'react-md/lib/ExpansionPanels'
import naturalSort from 'javascript-natural-sort'
import LoadingState from 'ui/components/LoadingState'
import type { LoanCreateFormInputs } from './validation'
import type { ControlledSelectableSharegroup } from '../types'

const AddProductPaymentButton = styled(Button)`
	display: flex;
	flex-flow: row nowrap;
	align-items: center;
	justify-content: center;
	margin-top: 0.5rem;
	gap: 0.5rem;
`

const CreateContractContainer = styled.div`
	display: flex;
	flex-flow: column nowrap;
	gap: 1rem;
	margin-top: 1rem;
	margin-bottom: 2rem;
`

const IconCircleContainer = styled.span<{ $backgroundColor: string }>`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 20px;
	height: 20px;
	border-radius: 50%;
	background-color: ${(props) => props.$backgroundColor};
`

const ContractPanelHeader = styled.div``

type Props = {
	sharegroups: ControlledSelectableSharegroup[]
	companyOptions: SelectOptions[]
}

function LoanCreateContract({ sharegroups, companyOptions }: Props) {
	const intl = useIntl()
	const { confirm } = useDialogs()

	const dispatch = useDispatch()

	const { setValue } = useFormContext<LoanCreateFormInputs>()

	const [processing, setProcessing] = useState(false)

	const [editingContract, setEditingContract] = useState<
		| {
				contractUUID: string
				apartmentUUID: string
				apartmentIdentifier: string
		  }
		| undefined
	>(undefined)

	const [addContract, setAddContract] = useState<
		{ apartmentUUID: string; apartmentIdentifier: string } | undefined
	>(undefined)

	const [sort, setSort] = useState<ContractSort>({
		key: 'apartmentUUID',
		type: 'apartmentOptions',
		order: 'asc',
	})

	const [companyUUID] = useWatch({
		name: ['companyUUID'],
	})

	const { buildings } = useBuildings(companyUUID)
	const { companyApartments } = useCompanyApartments(companyUUID)

	const chosenSharegroups = useMemo(() => {
		return sharegroups
			.filter((sg) => sg.selected)
			.sort((a, b) => naturalSort(a.identifier, b.identifier))
	}, [sharegroups])

	const chosenSharegroupUUIDs = useMemo(() => {
		return chosenSharegroups.map((apt) => apt.uuid)
	}, [chosenSharegroups])

	const financeFilters = useMemo(
		() => ({
			reviewDate: new Date(),
			active: true,
			bondName: '',
			apartmentUUID: '',
			invoicingMethod: '',
		}),
		[],
	)

	const { parties, refreshParties } = useParties(companyUUID, financeFilters)
	const shareholderPayerOptions = createPartyOptions(parties, intl, true)
	const apartmentOptions = createApartmentOptions(chosenSharegroups, buildings)
	const invoicingOptions: SelectOptions[] = Object.values(InvoicingMethod).map(
		(value) => ({
			label: invoicingMethodMessage(intl, value),
			value: value,
		}),
	)

	// We only want to only fetch contracts when apartment/sharegroup UUIDs have changed,
	// (not every time some field in apartment/sharegroup changes) which is why we have to store the ref and compare all UUIDs
	const previousUUIDsRef = useRef<string[]>([])
	const sameUUIDs =
		chosenSharegroupUUIDs.length === previousUUIDsRef.current.length &&
		chosenSharegroupUUIDs.every(
			(uuid, i) => uuid === previousUUIDsRef.current[i],
		)

	const contracts = useContractsByApartment(
		companyUUID,
		sameUUIDs ? previousUUIDsRef.current : chosenSharegroupUUIDs,
	)

	if (!sameUUIDs) {
		previousUUIDsRef.current = chosenSharegroupUUIDs
	}

	const changeSort = (key: ContractKey, type?: string) => {
		if (sort.key === key) {
			setSort((prevData) => ({
				...prevData,
				order: sort.order === 'asc' ? 'desc' : 'asc',
			}))
		} else {
			setSort({
				key: key,
				type: type || null,
				order: 'asc',
			})
		}
	}

	const deleteContract = async (uuid: string) => {
		const confirmMessage = intl.formatMessage({
			defaultMessage: 'Oletko varma, että haluat poistaa sopimuksen?',
			description: 'Confirm message for the delete contract.',
		})
		if (!(await confirm(confirmMessage))) {
			return
		}

		setProcessing(true)

		const result = await _deleteContract(uuid)
		setProcessing(false)

		if (!result || result.ok === false) {
			dispatch(notifyActions.error(GENERIC_ERROR_NOTIFICATION(intl)))
			return false
		}

		dispatch(
			notifyActions.success(
				intl.formatMessage({
					defaultMessage: 'Sopimus on poistettu.',
					description: 'Notification text contract has been deleted',
				}),
			),
		)
		contracts.refreshContracts()
		return true
	}

	const loading = contracts.loadingContracts

	return (
		<CreateContractContainer>
			<strong>
				<FormattedMessage
					defaultMessage="Lainan osakeryhmien sopimukset"
					description="Loan sharegroup contracts"
				/>
			</strong>
			{loading && <LoadingState />}
			{!loading && (
				<ExpansionList style={{ marginBottom: '2rem' }}>
					{chosenSharegroups.map((sg) => {
						const contractsForApartment = contracts.contracts.filter(
							(contract) => contract.apartmentUUID === sg.uuid,
						)
						const status = sg.contractUUID
							? 'ready'
							: contractsForApartment.length > 0
							? 'needs_action'
							: 'needs_contract'

						return (
							<ExpansionPanel
								key={`contract-list-panel-${sg.uuid}`}
								footer={null}
								headerStyle={{
									minHeight: '64px',
									padding: '0.5rem 1.5rem',
									boxSizing: 'content-box',
									height: 'auto',
								}}
								label={
									<div
										style={{
											display: 'flex',
											flexFlow: 'row',
											alignItems: 'center',
											gap: '1rem',
										}}
									>
										{status === 'needs_contract' && (
											<IconCircleContainer $backgroundColor="var(--color-error)">
												<FontIcon
													forceFontSize
													forceSize={20}
													style={{ color: 'white' }}
												>
													close
												</FontIcon>
											</IconCircleContainer>
										)}
										{status === 'needs_action' && (
											<IconCircleContainer $backgroundColor="var(--gray-6)">
												<FontIcon
													forceFontSize
													forceSize={20}
													style={{ color: 'white' }}
												>
													priority_high
												</FontIcon>
											</IconCircleContainer>
										)}
										{status === 'ready' && (
											<IconCircleContainer $backgroundColor="var(--color-success)">
												<FontIcon
													forceFontSize
													forceSize={20}
													style={{ color: 'white' }}
												>
													done
												</FontIcon>
											</IconCircleContainer>
										)}
										<ContractPanelHeader>
											<p>{sg.identifier}</p>
											<div
												style={{
													display: 'flex',
													flexFlow: 'row nowrap',
													gap: '0.25rem',
												}}
											>
												{contractsForApartment.length === 0 && (
													<FormattedMessage
														defaultMessage="Sopimuksia ei löytynyt huoneistolle."
														description="No contracts were found for apartment"
													/>
												)}
												{contractsForApartment.length > 0 && (
													<span>
														<FormattedMessage
															defaultMessage="Sopimuksia löytyi huoneistolle {contractAmount} kappaletta."
															description="Found X contracts for the apartment"
															values={{
																contractAmount: contractsForApartment.length,
															}}
														/>
													</span>
												)}
												{status === 'needs_action' && (
													<span>
														<FormattedMessage
															defaultMessage="Valitse mitä sopimusta käytät tällä lainalla."
															description="Found X contracts for the apartment"
															values={{
																contractAmount: contractsForApartment.length,
															}}
														/>
													</span>
												)}
												{status === 'needs_contract' && (
													<span>
														<FormattedMessage
															defaultMessage="Luo uusi sopimus huoneistolle."
															description="Found X contracts for the apartment"
															values={{
																contractAmount: contractsForApartment.length,
															}}
														/>
													</span>
												)}
											</div>
										</ContractPanelHeader>
									</div>
								}
							>
								<ContractTable
									key={`contract-table-${sg.uuid}`}
									id={`contract-table-id-${sg.uuid}`}
									contracts={contractsForApartment}
									onClickContract={(contractUUID) =>
										setEditingContract({
											contractUUID,
											apartmentUUID: sg.uuid,
											apartmentIdentifier: sg.identifier,
										})
									}
									onDeleteContract={deleteContract}
									sort={sort}
									changeSort={changeSort}
									apartmentOptions={apartmentOptions}
									companyOptions={companyOptions}
									processing={processing}
									selectedContractUUID={
										sg.contractUUID ? sg.contractUUID : undefined
									}
									onContractSelected={(uuid) => {
										setValue(
											`selectableSharegroups.${sg._trueIndex}.contractUUID`,
											sg.contractUUID
												? uuid !== sg.contractUUID
													? uuid
													: undefined
												: uuid,
										)
									}}
								/>
								{status !== 'ready' && (
									<div>
										<AddProductPaymentButton
											secondary
											flat
											onClick={() =>
												setAddContract({
													apartmentUUID: sg.uuid,
													apartmentIdentifier: sg.identifier,
												})
											}
										>
											<FontIcon
												style={{
													color: 'var(--color-secondary-dark)',
												}}
											>
												add
											</FontIcon>
											<FormattedMessage
												defaultMessage="Lisää uusi sopimus"
												description="Button label for adding new contract"
											/>
										</AddProductPaymentButton>
									</div>
								)}
							</ExpansionPanel>
						)
					})}
				</ExpansionList>
			)}

			{addContract && (
				<ContractDialog
					useContracts={contracts}
					onHide={() => setAddContract(undefined)}
					selectedFinanceCompanyUUID={companyUUID}
					selectedApartment={{
						uuid: addContract.apartmentUUID,
						identifier: addContract.apartmentIdentifier,
					}}
					companyOptions={companyOptions}
					payerOptions={shareholderPayerOptions}
					apartmentOptions={apartmentOptions.filter(
						(option) => option.value === addContract.apartmentUUID,
					)}
					invoicingOptions={invoicingOptions}
					companyApartments={companyApartments}
					refreshParties={refreshParties}
					onlyShareholders
				/>
			)}
			{editingContract && (
				<ContractDialog
					id={editingContract.contractUUID}
					useContracts={contracts}
					onHide={() => setEditingContract(undefined)}
					selectedFinanceCompanyUUID={companyUUID}
					selectedApartment={{
						uuid: editingContract.apartmentUUID,
						identifier: editingContract.apartmentIdentifier,
					}}
					companyOptions={companyOptions}
					payerOptions={shareholderPayerOptions}
					apartmentOptions={apartmentOptions.filter(
						(option) => option.value === editingContract.apartmentUUID,
					)}
					invoicingOptions={invoicingOptions}
					companyApartments={companyApartments}
					refreshParties={refreshParties}
					onlyShareholders
				/>
			)}
			<p>
				<FormattedMessage
					defaultMessage="Sopimuksia on määritelty {definedContracts} / {maxContracts}."
					description="Contracts have been defined for {definedContracts} / {maxContracts}."
					values={{
						definedContracts: chosenSharegroups.filter(
							(sg) => sg.contractUUID !== null && sg.contractUUID !== undefined,
						).length,
						maxContracts: chosenSharegroups.length,
					}}
				/>
			</p>
		</CreateContractContainer>
	)
}

export default LoanCreateContract
